import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "../../_css/VoiceChatPopup.module.css";
import { X, FileText, PhoneOff, Pause, Play } from "lucide-react";
import DeleteUserModal from "../Modals/DeleteUserModal";
import {
  startChat,
  transcribeAudio,
  chatMessage,
  updateSessionDuration,
  endSession,
} from "../../requests";
import { deleteAuthToken, notifyError } from "../../utils/helper";
import ResponseLoader from "../common/ResponseLoader";
// import AudioReactRecorder, { RecordState } from "audio-react-recorder"; // Removed - using STT streaming instead
import { createPortal } from "react-dom";
import {
  VOICE_CHAT_POPUP_MODAL,
  DOWNLOAD_SUMMARY_MODAL,
} from "../../data/modelsData";
import { useSelector } from "react-redux";
import { CustomButton, CustomButtonOutline } from "../UI/CustomButtons";
import {
  isHTML,
  formatHTMLResponse,
  getLanguageCode,
} from "../helper/formatters";
import SpeechToText from "../common/SpeechToText";
import ERROR_MSGS from "../../data/errorMsgs";
import useSessionTimer from "../hooks/useSessionTimer";

const DEFAULT_TIMER_MINUTES = 45;

const USER_IMAGE = `${process.env.PUBLIC_URL}/img/user-icon.png`;
const DR_AVATAR = `${process.env.PUBLIC_URL}/img/dr-salah-icon.png`;

const VoiceChatPopup = ({
  show,
  onClose,
  setShowDownloadSummary,
  header,
  refreshReviews,
  onDownload,
  pdfStatus,
}) => {
  const lang = useSelector((state) => state.language.lang);
  const {
    headTitle,
    btnText,
    confirmText,
    btnText2,
    cancelText,
    continueSessionText,
    sessionCantBeContinuedText,
  } = VOICE_CHAT_POPUP_MODAL[lang];
  const { btnText: btnText3 } = DOWNLOAD_SUMMARY_MODAL[lang];

  const [micDisabled, setMicDisabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [hovered, setHovered] = useState("");
  const [summary, setSummary] = useState(false);
  const [transcript, setTranscript] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [audioData, setAudioData] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  // const [recordState, setRecordState] = useState(null); // Removed - using STT streaming instead
  const [isRecording, setIsRecording] = useState(false);
  const [generatingTranscript, setGeneratingTranscript] = useState(false);
  const [userSessionId, setUserSessionId] = useState(null);
  const [sessionHistory, setSessionHistory] = useState(false);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [isAIResponseComplete, setIsAIResponseComplete] = useState(true);
  const [isSessionPaused, setIsSessionPaused] = useState(false);
  const [isQueryLocked, setIsQueryLocked] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const [initialSpentSeconds, setInitialSpentSeconds] = useState(0);

  const { timerText: timer, spentSeconds, setPaused, reset: resetTimer } =
    useSessionTimer({
      show,
      userSessionId,
      totalSeconds: DEFAULT_TIMER_MINUTES * 60,
      initialSpentSeconds,
      isPaused: isSessionPaused,
      onExpire: () => {
        onClose?.();
        setShowDownloadSummary(true);
      },
      onDurationChange: (duration) => {
        if (!userSessionId) return;
        updateSessionDuration({
          userSessionId,
          duration,
        });
      },
    });

  useEffect(() => {
    const shouldDisable =
      waiting || isSpeaking || !isAIResponseComplete || isSessionPaused;

    // Add a small delay before enabling to prevent rapid state changes
    if (!shouldDisable) {
      const timeout = setTimeout(() => {
        setMicDisabled(false);
      }, 100);
      return () => clearTimeout(timeout);
    } else {
      setMicDisabled(true);
    }
  }, [waiting, isSpeaking, isAIResponseComplete, isSessionPaused]);

  // STT Streaming callback
  const handleTranscriptReady = (transcript) => {
    handleSend(transcript);
  };

  const sessionData = sessionStorage.getItem("sessionData");
  const sessionDataParsed = JSON.parse(sessionData);
  const sessionId = sessionDataParsed?.id;

  const userInfo = JSON.parse(sessionStorage.getItem("sal_userInfo"));
  const userName = userInfo?.fullName || "User";

  const audioPlayer = useRef(null); // Ref for the audio element
  const chatEndRef = useRef(null);
  const sttStreamingRef = useRef(null); // Ref for STT streaming component

  // transcript effect
  useEffect(() => {
    if (sessionHistory) {
      setTranscript(true);
    } else {
      setTranscript(false);
    }
  }, [sessionHistory]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    if (audioData) {
      playAudio();
    }
  }, [messages, audioData]);

  // No need for STT cleanup - handled by STTStreaming component

  // Pause/resume is handled inside useSessionTimer (single source of truth)

  useEffect(() => {
    // resume session block
    let sessionResume = false;
    const resumeSessionData = sessionStorage.getItem("resumeSessionData");
    const chatHistory = sessionStorage.getItem("chatHistory");

    if (resumeSessionData && chatHistory) {
      sessionResume = true;
      const resumeSessionDataParsed = JSON.parse(resumeSessionData);
      setSessionHistory(true);
      setSessionStatus(resumeSessionDataParsed?.status);

      // set user session id
      const userSessionId = resumeSessionDataParsed?.userSessionId;
      sessionStorage.setItem("userSessionId", userSessionId);
      setUserSessionId(userSessionId);

      // initialize timer from backend duration (spent seconds)
      const duration = Number(resumeSessionDataParsed?.duration || 0);
      setInitialSpentSeconds(Number.isFinite(duration) ? duration : 0);

      // set chat history
      const chatHistoryParsed = JSON.parse(chatHistory);
      setMessages(chatHistoryParsed);
      // setTranscript(true);
    } else {
      setSessionHistory(false);
      setInitialSpentSeconds(0);
    }

    if (show) {
      if (!sessionResume) {
        handleStartChat();
      }
    } else {
      resetStates(false);
    }
    return () => {
      resetStates(false);
    };
  }, [show]);

  const resetStates = (clearTimer = false) => {
    // Best-effort final duration sync before UI teardown
    if (userSessionId && spentSeconds > 0) {
      updateSessionDuration({
        userSessionId,
        duration: spentSeconds,
      });
    }

    setMessages([]);
    setWaiting(false);
    setHovered("");
    setSummary(false);
    setAudioData(null);
    stopAudioPlaying();
    setSessionHistory(false);
    setSessionStatus(null);
    setIsSessionPaused(false);
    // Disable mic when resetting states
    if (sttStreamingRef.current) {
      sttStreamingRef.current.disableMic();
    }
    setUserSessionId(null);
    setInitialSpentSeconds(0);
    if (clearTimer) {
      resetTimer();
    }
  };

  const stopAudioPlaying = async () => {
    if (audioPlayer.current) {
      audioPlayer.current.pause();
      // Optionally, you might want to reset the currentTime to 0
      // audioPlayer.current.currentTime = 0;
      // However, revoking the URL is important for resource management
      if (audioPlayer.current.src) {
        URL.revokeObjectURL(audioPlayer.current.src);
      }
      audioPlayer.current = null; // Clear the ref
    }
  };

  const playAudio = () => {
    let audio = audioData?.audio_data;
    if (!audio) {
      setIsAIResponseComplete(true);
      return;
    }

    // Clean up any existing audio player first
    if (audioPlayer.current) {
      try {
        audioPlayer.current.pause();
        if (audioPlayer.current.src) {
          URL.revokeObjectURL(audioPlayer.current.src);
        }
      } catch (e) {
        console.warn("Error cleaning up previous audio:", e);
      }
      audioPlayer.current = null;
    }

    // Play audio
    const audioBlob = new Blob(
      [Uint8Array.from(atob(audio), (c) => c.charCodeAt(0))],
      { type: "audio/mpeg" }
    );
    const url = URL.createObjectURL(audioBlob);
    const audioElement = new Audio(url);
    audioPlayer.current = audioElement;

    // Set speaking true when audio starts
    audioElement.onplay = () => {
      setIsSpeaking(true);
    };

    // Set speaking false when audioElement ends
    audioElement.onended = () => {
      console.log("🔊 Audio ended - unlocking query and enabling mic");
      setIsSpeaking(false);
      setAudioData(null);
      setIsQueryLocked(false);
      setIsAIResponseComplete(true); // AI finished speaking, mic can be enabled by user
      
      // Clean up blob URL
      if (audioElement.src) {
        URL.revokeObjectURL(audioElement.src);
      }
    };

    audioElement.onerror = (e) => {
      console.error("audioElement error:", e);
      console.log("🔊 Audio error - unlocking query and enabling mic");
      setIsSpeaking(false);
      setAudioData(null);
      setIsQueryLocked(false);
      setIsAIResponseComplete(true); // Mic can be enabled by user on error too
      
      // Clean up blob URL
      if (audioElement.src) {
        URL.revokeObjectURL(audioElement.src);
      }
    };

    // iOS fix: Explicitly load audio and wait for it to be ready before playing
    // This works on all platforms but is critical for iOS
    const attemptPlay = () => {
      audioElement.play().catch((err) => {
        console.error("Playback failed:", err);
        console.log("🔊 Playback failed - unlocking query and enabling mic");
        setIsSpeaking(false);
        setAudioData(null);
        setIsQueryLocked(false);
        setIsAIResponseComplete(true); // Mic can be enabled by user on playback failure
        
        // Clean up blob URL
        if (audioElement.src) {
          URL.revokeObjectURL(audioElement.src);
        }
      });
    };

    // Load the audio first (required for iOS)
    audioElement.load();

    // Wait for audio to be ready before playing
    // Use canplaythrough for best compatibility, with fallback to loadeddata
    let hasAttemptedPlay = false;
    
    // Fallback timeout in case events don't fire (shouldn't happen, but safety net)
    const timeoutId = setTimeout(() => {
      if (!hasAttemptedPlay) {
        console.warn("Audio ready event timeout, attempting playback anyway");
        hasAttemptedPlay = true;
        attemptPlay();
      }
    }, 3000);

    const playWhenReady = () => {
      if (!hasAttemptedPlay) {
        hasAttemptedPlay = true;
        clearTimeout(timeoutId);
        attemptPlay();
      }
    };

    // Try multiple events for maximum compatibility
    audioElement.addEventListener("canplaythrough", playWhenReady, { once: true });
    audioElement.addEventListener("loadeddata", playWhenReady, { once: true });
  };

  const handleStartChat = async () => {
    setWaiting(true);
    setIsAIResponseComplete(false); // AI is speaking
    try {
      const response = await startChat({
        sessionId,
        voice: true,
        lang,
      });
      // Trigger a refresh of the current page to get updated data
      const aiText = response.data.message;
      const userSessionId = response.data.userSessionId;
      const audioData = response.data.voice_data;

      sessionStorage.setItem("userSessionId", userSessionId);
      setUserSessionId(userSessionId);
      setMessages([{ sender: "dr", text: aiText }]);
      setInput("");
      setAudioData(audioData);
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      } else if (error?.response?.status === 400) {
        notifyError(error?.response?.data?.message[lang]);
      }
      setIsAIResponseComplete(true);
      console.error("Error Initiating Chat:", error);
      let aiText = ERROR_MSGS[lang].chatStartError;
      setMessages([{ sender: "dr", text: aiText }]);
      setInput("");
      setUserSessionId(null);
      sessionStorage.removeItem("userSessionId");
    }
    setWaiting(false);
  };

  const handleSend = async (transcript) => {
    if (isSpeaking || waiting) {
      return;
    }
    setIsAIResponseComplete(false); // AI is speaking
    setIsQueryLocked(true); // Lock query when sending

    // Auto-resume session if paused when user sends a message
    if (isSessionPaused) {
      setIsSessionPaused(false);
    }

    // Disable mic after sending message
    if (sttStreamingRef.current) {
      sttStreamingRef.current.disableMic();
    }

    // if (!blob) {
    //   notifyError("Please record your message first.");
    //   return;
    // }
    // to regenerate summary
    if (summary) {
      setSummary(false);
    }

    // let transcript = await handleAudioTranscript(blob);
    if (!transcript) {
      transcript = ERROR_MSGS[lang].transcriptError;
      setMessages((msgs) => [...msgs, { sender: userName, text: transcript }]);
      return;
    }

    // STT streaming is now handled internally by STTStreaming component

    setMessages((msgs) => [...msgs, { sender: userName, text: transcript }]);
    setWaiting(true);
    const aiText = await handleChat(transcript);
    setMessages((msgs) => [...msgs, { sender: "dr", text: aiText }]);
    setWaiting(false);
  };

  const handleAudioTranscript = async (blob) => {
    setGeneratingTranscript(true);
    try {
      const formData = new FormData();

      // Determine the correct file extension based on the blob type
      let fileName = "audio.webm";
      if (blob.type.includes("wav")) {
        fileName = "audio.wav";
      } else if (blob.type.includes("mp3")) {
        fileName = "audio.mp3";
      } else if (blob.type.includes("m4a")) {
        fileName = "audio.m4a";
      }

      formData.append("file", blob, fileName);
      formData.append("session_id", userSessionId);
      formData.append("language", lang);
      const response = await transcribeAudio(formData);
      const aiText = response.data?.transcription;
      setGeneratingTranscript(false);
      return aiText;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
    setGeneratingTranscript(false);
  };

  const handleChat = async (userMessage) => {
    let sessionId = userSessionId;
    if (!sessionId) {
      sessionId = sessionStorage.getItem("userSessionId");
      if (!sessionId) {
        return ERROR_MSGS[lang].chatStartError;
      }
      setUserSessionId(sessionId);
    }

    // return lang === "ar"
    // ? "حدث خطأ ما ، يرجى المحاولة مرة أخرى"
    // : "Something went wrong, please try again.";

    try {
      const requestData = {
        userSessionId: sessionId,
        user_msg: userMessage,
        voice: true,
      };
      const response = await chatMessage(requestData);
      const aiText = response.data.message;
      const audioData = response.data.voice_data;
      setAudioData(audioData);
      return aiText;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.log("🔊 No audio found, unlocking query and enabling mic");
      setIsSpeaking(false);
      setAudioData(null);
      setIsQueryLocked(false);
      setIsAIResponseComplete(true); // AI finished speaking, mic can be enabled by user
   

      console.error("Error Sending Message:", error);
      return lang === "ar"
        ? "حدث خطأ ما ، يرجى المحاولة مرة أخرى"
        : "Something went wrong, please try again.";
    }
  };

  // No need for recording start/stop functions - handled by STTStreaming component

  // const handleTranscriptReceived = async (transcript) => {
  //   if (!transcript || transcript.trim() === "") return;

  //   // Add transcript to messages
  //   setMessages((msgs) => [...msgs, { sender: userName, text: transcript }]);

  //   // Generate AI response
  //   setWaiting(true);
  //   try {
  //     const aiText = await handleChat(transcript);
  //     setMessages((msgs) => [...msgs, { sender: "dr", text: aiText }]);
  //   } catch (error) {
  //     console.error("Error generating AI response:", error);
  //   }
  //   setWaiting(false);
  // };

  // Cleanup when popup closes
  useEffect(() => {
    if (!show && sttStreamingRef.current) {
      console.log("Popup closed, cleaning up STT streaming...");
      sttStreamingRef.current.cleanup();
    }
  }, [show]);

  const handleEndSession = async () => {
    try {
      const response = await endSession({
        userSessionId,
      });
      if (response.status === 200) {
        refreshReviews();
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Initiating Chat:", error);
    }
    // session ended -> clear persisted timer state
    resetStates(true);
    setShowDownloadSummary(true);
    setShowDeleteUserModal(false);
  };

  const handleResumeSession = () => {
    setSessionHistory(false);
    setMicDisabled(false);
  };

  const handlePauseResumeSession = () => {
    setIsSessionPaused(!isSessionPaused);
  };

  const handleSpeechQuery = (text) => {
    console.log("text: ", text);
  };

  // No need for auto-control - STTStreaming component handles this internally

  return (
    <>
      {show && (
        <div className={styles.overlay}>
          <div
            style={{
              maxHeight: transcript ? "50rem" : "30rem",
              marginTop: 0,
            }}
            className={styles.popup}
          >
            <div
              className={`${styles.mainContainer} ${
                transcript ? styles.withTranscript : ""
              }`}
            >
              <div
                className={`${styles.voiceChatUI} ${
                  transcript ? styles.voiceChatUIShifted : ""
                }`}
              >
                {!transcript && (
                  <button className={styles.closeBtn} onClick={onClose}>
                    <X size={22} />
                  </button>
                )}
                <div
                  // style={{
                  //   width: transcript ? "100%" : "50%",
                  // }}
                  className={styles.voiceChatUIContent}
                >
                  <div className={isSpeaking ? "call-pulse" : ""}>
                    <img
                      src={`${process.env.PUBLIC_URL}/img/dr-slah-3.png`}
                      alt="dr-salah"
                    />
                  </div>

                  <p>{headTitle}</p>
                  <h4>{header}</h4>
                  {/* STT errors are now handled internally by STTStreaming component */}
                </div>
              </div>
              <div
                className={`${styles.chatBody} ${
                  transcript ? styles.chatBodyVisible : styles.chatBodyHidden
                }`}
              >
                <div className={styles.chatBodyHeader}>
                  <h5 className={styles.title}>{btnText[0]}</h5>
                  <button
                    className={styles.closeBtnTranscript}
                    onClick={onClose}
                  >
                    <X size={22} />
                  </button>
                </div>
                <hr className="m-0" />
                {transcript && (
                  <div className={styles.chatBodyContent}>
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={
                          msg.sender === userName ? styles.drMsg : styles.aiMsg
                        }
                      >
                        <div
                          className={styles.msgText}
                          style={{ overflowX: "auto" }}
                        >
                          <div className={styles.avatar}>
                            <i>
                              <img
                                src={
                                  msg.sender === userName
                                    ? USER_IMAGE
                                    : DR_AVATAR
                                }
                                alt="avatar"
                              />
                            </i>

                            <span>
                              {msg.sender === userName ? userName : ""}
                            </span>
                          </div>
                          <div className="m-0 mt-2">
                            {isHTML(msg.text) ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: formatHTMLResponse(msg.text),
                                }}
                              />
                            ) : (
                              <p className="m-0">{msg.text}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {waiting && (
                      <div className={styles.aiMsg}>
                        <div className={styles.msgText}>
                          <div className={styles.avatar}>
                            <i>
                              <img src={DR_AVATAR} alt="avatar" />
                            </i>
                          </div>

                          <ResponseLoader />
                        </div>
                      </div>
                    )}
                    {generatingTranscript && (
                      <div className={styles.drMsg}>
                        <div className={styles.msgText}>
                          <div className={styles.avatar}>
                            <i>
                              <img src={USER_IMAGE} alt="avatar" />
                            </i>

                            <span>{userName}</span>
                          </div>

                          <ResponseLoader />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </div>
            </div>
            <hr className="m-0" />
            {!sessionHistory ? (
              <div className={styles.inputRow}>
                <div className={styles.bottomBar}>
                  <span className={styles.bottomIconTime}>{timer}</span>
                  <div
                    onMouseEnter={() => setHovered("transcript")}
                    onMouseLeave={() => setHovered("")}
                    className={
                      styles.bottomIconWrap +
                      (transcript ? " " + styles.active : "")
                    }
                    onClick={() => setTranscript(!transcript)}
                  >
                    <FileText className={styles.bottomIcon} />
                    <span
                      className={
                        styles.bottomTextAnimated + " " + hovered ===
                        "transcript"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      {btnText[0]}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {/* AI Response Status Indicator */}
                    {/* {waiting && (
                      <div className="d-flex align-items-center gap-1">
                        <div
                          className="spinner-border spinner-border-sm text-success"
                          role="status"
                        >
                          <span className="visually-hidden">Generating...</span>
                        </div>
                        <small className="text-muted">
                          Generating response...
                        </small>
                      </div>
                    )} */}
                    {/* STT Streaming Component */}
                    {show && (
                      <>
                        {/* <i
                          className="pe-cursor"
                          onClick={() => setIsRunning(!isRunning)}
                        >
                          {!isRunning ? (
                            <CirclePlay color="#23b4c3" />
                          ) : (
                            <CirclePause color="#23b4c3" />
                          )}
                        </i> */}
                        <i className="pe-cursor">
                          <SpeechToText
                            handleQuery={handleTranscriptReady}
                            isQueryLockedProp={isQueryLocked}
                            isDisabled={micDisabled}
                            language={getLanguageCode(lang)}
                            aiAudioCompleted={isAIResponseComplete}
                            styles={styles}
                          />
                        </i>
                      </>
                    )}
                    {/* {show && (
                      <LiveTranscribe
                        ref={sttStreamingRef}
                        onTranscriptReady={handleTranscriptReady}
                        disabled={micDisabled}
                        className=""
                        styles={styles}
                        language={lang}
                      />
                    )} */}
                    {/* <button
                    onClick={recordingStart}
                    className="record-icon box-shadow"
                  >
                    <Mic strokeWidth={1} size={20} color="#0E46A3" />
                  </button>
                  ) : (
                  <button onClick={recordingStop}>
                    <StopCircle size={28} color="#0e46a3" />
                  </button> */}
                  </div>
                  {/* <div
                  onClick={() => setMuted(!muted)}
                  className={
                    styles.bottomIconWrap + (muted ? " " + styles.active : "")
                  }
                  onMouseEnter={() => setHovered("mic")}
                  onMouseLeave={() => setHovered("")}
                >
                  {muted ? (
                    <MicOff className={styles.bottomIcon} />
                  ) : (
                    <Mic className={styles.bottomIcon} />
                  )}
                  <span
                    className={
                      hovered === "mic"
                        ? styles.bottomTextShow
                        : styles.bottomTextHide
                    }
                  >
                    {muted ? "Unmute" : "Mute"}
                  </span>
                </div> */}
                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("pauseResume")}
                    onMouseLeave={() => setHovered("")}
                    onClick={handlePauseResumeSession}
                  >
                    {isSessionPaused ? (
                      <Play className={styles.bottomIcon} />
                    ) : (
                      <Pause className={styles.bottomIcon} />
                    )}
                    <span
                      className={
                        styles.bottomTextAnimated + " " + hovered ===
                        "pauseResume"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      {isSessionPaused ? "Resume Session" : "Pause Session"}
                    </span>
                  </div>

                  <div
                    className={styles.bottomIconWrapDanger}
                    onMouseEnter={() => setHovered("endSession")}
                    onMouseLeave={() => setHovered("")}
                    onClick={() => setShowDeleteUserModal(true)}
                  >
                    <PhoneOff className={styles.bottomIcon} />
                    <span
                      className={
                        styles.bottomTextAnimated + " " + hovered ===
                        "endSession"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      {btnText[2]}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`${styles.inputRowSummary} d-flex justify-content-between`}
                style={{ padding: "8px" }}
              >
                <span>
                  {sessionStatus === 0
                    ? continueSessionText
                    : sessionCantBeContinuedText}
                </span>

                {/* transcript */}
                <div
                  onMouseEnter={() => setHovered("transcript")}
                  onMouseLeave={() => setHovered("")}
                  className={
                    styles.bottomIconWrap +
                    (transcript ? " " + styles.active : "")
                  }
                  onClick={() => setTranscript(!transcript)}
                >
                  {/* <FileText className={styles.bottomIcon} /> */}
                  <span
                    className={
                      styles.bottomTextAnimated + " " + hovered === "transcript"
                        ? styles.bottomTextShow
                        : styles.bottomTextHide
                    }
                  >
                    {btnText[0]}
                  </span>
                </div>

                <div className={`d-flex gap-2 ${styles.buttonRowSummary}`}>
                  <CustomButtonOutline
                    text={lang === "ar" ? "استمرار الجلسة" : "Resume Session"}
                    onClick={handleResumeSession}
                    width="100%"
                    disabled={sessionStatus !== 0}
                  />
                  <CustomButton
                    icon="download"
                    gap="8px"
                    text={pdfStatus || btnText3[1]}
                    onClick={onDownload}
                    width="100%"
                    disabled={pdfStatus}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showDeleteUserModal &&
        createPortal(
          <DeleteUserModal
            show={showDeleteUserModal}
            onHide={() => setShowDeleteUserModal(false)}
            onEndSession={handleEndSession}
            title={confirmText}
            btnText={btnText[2]}
            cancelText={cancelText}
          />,
          document.body
        )}
    </>
  );
};

export default VoiceChatPopup;
